import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { ObjectUploader } from "./ObjectUploader";
import { toast } from "sonner";
import { Upload, FileText, File, Image, Eye, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

type DocumentType = "job_description" | "resume" | "dossier" | "other";

interface DocumentUploaderProps {
  documentType: DocumentType;
  roleId?: number;
  candidateId?: number;
  employerId?: number;
  applicationId?: number;
  onUploadComplete?: (document: any) => void;
  buttonText?: string;
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  existingUrl?: string | null;
}

const FILE_TYPE_ICONS: Record<string, any> = {
  "application/pdf": FileText,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileText,
  "application/msword": FileText,
  "image/jpeg": Image,
  "image/png": Image,
  default: File,
};

export function DocumentUploader({
  documentType,
  roleId,
  candidateId,
  employerId,
  applicationId,
  onUploadComplete,
  buttonText = "Upload Document",
  acceptedFileTypes,
  maxFileSize = 25 * 1024 * 1024,
  existingUrl,
}: DocumentUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [pendingDocumentId, setPendingDocumentId] = useState<number | null>(null);
  const [showUploader, setShowUploader] = useState(!existingUrl);

  const handleGetUploadParameters = async (file: any) => {
    setIsUploading(true);
    
    try {
      const response = await apiRequest("POST", "/api/agent/documents/request-upload-url", {
          filename: file.name,
          mimeType: file.type,
          documentType,
          roleId,
          candidateId,
          employerId,
          applicationId,
        });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to get upload URL");
      }

      const { documentId, uploadURL } = await response.json();
      setPendingDocumentId(documentId);

      return {
        method: "PUT" as const,
        url: uploadURL,
        headers: {
          "Content-Type": file.type,
        },
      };
    } catch (error) {
      setIsUploading(false);
      toast.error(error instanceof Error ? error.message : "Upload failed");
      throw error;
    }
  };

  const handleComplete = async (result: any) => {
    if (result.successful.length > 0 && pendingDocumentId) {
      try {
        const confirmResponse = await apiRequest("POST", `/api/agent/documents/${pendingDocumentId}/confirm-upload`, {
            sizeBytes: result.successful[0].size,
          });

        if (confirmResponse.ok) {
          const { document } = await confirmResponse.json();
          toast.success("Document uploaded successfully");
          onUploadComplete?.(document);
        } else {
          toast.error("Failed to confirm upload");
        }
      } catch (error) {
        toast.error("Failed to confirm upload");
      }
    }
    
    setIsUploading(false);
    setPendingDocumentId(null);
  };

  if (existingUrl && !showUploader) {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(existingUrl, "_blank")}
          className="text-green-600 border-green-600 hover:bg-green-50 dark:text-green-400 dark:border-green-400 dark:hover:bg-green-950"
          data-testid="button-view-document"
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowUploader(true)}
          className="text-muted-foreground hover:text-foreground"
          data-testid="button-replace-document"
          title="Replace document"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <ObjectUploader
      maxNumberOfFiles={1}
      maxFileSize={maxFileSize}
      onGetUploadParameters={handleGetUploadParameters}
      onComplete={(result) => {
        handleComplete(result);
        setShowUploader(false);
      }}
      buttonClassName={isUploading ? "opacity-50 pointer-events-none" : ""}
    >
      <Upload className="h-4 w-4 mr-2" />
      {isUploading ? "Uploading..." : buttonText}
    </ObjectUploader>
  );
}
